/**
 * Tester session store (uat-portal-suite).
 *
 * Stories: epic-uat-portal-001-8 (introduced), epic-uat-portal-001-15 (persistence).
 *
 * Holds the standalone UAT-portal session — the token returned by
 * `POST /api/uat/auth/redeem` / `/verify-link` plus the account record — so the
 * portal can present `Authorization: Bearer <sessionToken>` to the oracle-bridge
 * `/api/uat/*` tester-gated endpoints and greet the tester by name.
 *
 * PERSISTENCE (-15). The session is persisted in `localStorage`. The -8 version
 * kept it IN MEMORY ONLY, copying dao-suite's `identity-jwt-store` XSS posture —
 * but dao-suite tolerates a lost in-memory token because it has a SILENT REFRESH
 * (identity-service). The UAT portal has none: its only re-auth is a magic link.
 * So in-memory meant every refresh, and every guide link that opened the portal
 * in a NEW TAB, landed unauthenticated and forced a fresh magic link — the
 * portal was barely usable. We persist instead.
 *
 * Why this is a proportionate posture here (NOT a regression of -8's intent):
 *   - the account is a lightweight UAT TESTER account — not a member, no IC
 *     principal, no funds, no governance; the whole portal is staging-only;
 *   - the session is SERVER-SIDE REVOCABLE and short-lived — every 401 in
 *     `authenticatedFetch` clears the session and bounces to `/login`, so a
 *     stolen/stale token stops working the moment the backend says so;
 *   - `localStorage` (not `sessionStorage`) because the reported break is a
 *     link opening a NEW TAB — `sessionStorage` is per-tab and would not carry
 *     the session there.
 *
 * Storage is accessed defensively: a private-mode / storage-disabled browser
 * (where `localStorage` access throws) DEGRADES to an in-memory session for the
 * page's lifetime — still works, just not persisted — and never throws.
 *
 * This file is DISTINCT from member identity: the UAT portal uses its OWN
 * lightweight account (not `@hello-world-dao/auth` / identity-service). Nothing
 * here touches an IC principal or an ES256 JWT.
 */

import type { TesterAccount } from './testerAuthContext';

const TOKEN_KEY = 'uat_portal_session_token';
const ACCOUNT_KEY = 'uat_portal_session_account';

/** Whether `localStorage` is usable — probed once (private-mode never flips mid-session). */
const STORAGE_OK: boolean = (() => {
  try {
    const probe = '__uat_portal_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
})();

// In-memory mirror. Authoritative ONLY when storage is unavailable; otherwise
// localStorage is the source of truth (so a logout in another tab is honored).
let memToken: string | null = null;
let memAccount: TesterAccount | null = null;

function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // Storage disabled/full — the in-memory mirror still carries the session.
  }
}

// Listeners notified on any session change, so the provider's auth state stays a
// single source of truth even when the session is cleared OUTSIDE the provider
// (authenticatedFetch's 401 path) or in ANOTHER tab (storage event below).
const listeners = new Set<() => void>();
function notify(): void {
  for (const l of listeners) l();
}

// Cross-tab coherence: when another tab writes the token/account (login/logout),
// or clears storage (key === null), re-notify so this tab's provider resyncs.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('storage', (e: StorageEvent) => {
    if (e.key === TOKEN_KEY || e.key === ACCOUNT_KEY || e.key === null) notify();
  });
}

/** Subscribe to session changes; returns an unsubscribe fn. */
export function subscribeToken(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Store the tester session token (after a successful redeem / verify-link).
 * Pass null/empty to clear ONLY the token (prefer {@link clearTesterToken} for
 * a full session end, which also drops the account).
 */
export function setTesterToken(token: string | null): void {
  const next = token && token.length > 0 ? token : null;
  memToken = next;
  if (STORAGE_OK) writeStored(TOKEN_KEY, next);
  notify();
}

/** Get the current tester session token, or null when none is set. */
export function getTesterToken(): string | null {
  return STORAGE_OK ? readStored(TOKEN_KEY) : memToken;
}

/**
 * Clear the whole tester session — token AND account (logout / 401 / expiry).
 * A session is one unit: a dead token must never leave a stale account behind.
 */
export function clearTesterToken(): void {
  memToken = null;
  memAccount = null;
  if (STORAGE_OK) {
    writeStored(TOKEN_KEY, null);
    writeStored(ACCOUNT_KEY, null);
  }
  notify();
}

/** True when a tester session token is currently available to send. */
export function hasTesterToken(): boolean {
  return getTesterToken() !== null;
}

/** Persist the account record alongside the token (null clears just the account). */
export function setPersistedAccount(account: TesterAccount | null): void {
  memAccount = account;
  if (STORAGE_OK) writeStored(ACCOUNT_KEY, account ? JSON.stringify(account) : null);
  notify();
}

/**
 * The persisted account, or null when none / unparseable. Used by the provider
 * to rehydrate the tester's displayName on a fresh load.
 */
export function getPersistedAccount(): TesterAccount | null {
  if (!STORAGE_OK) return memAccount;
  const raw = readStored(ACCOUNT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof (parsed as TesterAccount).id === 'string' &&
      typeof (parsed as TesterAccount).email === 'string' &&
      typeof (parsed as TesterAccount).displayName === 'string'
    ) {
      return parsed as TesterAccount;
    }
    return null;
  } catch {
    return null;
  }
}
